// Data-driven Arabic inner-page renderer. Mirrors the English
// <InnerPage /> but with Arabic UI chrome (hero button text, related
// heading, CTA copy). Every AR services / industries page is a small
// wrapper that hands this component a `sections` array.
import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Plus, Minus } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ArBlock, ArGrid, ArBullets, ArContactCTA } from "@/pages/ar/_ArShared";
import { cn } from "@/lib/utils";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { WA_SERVICE_LABEL } from "@/lib/whatsapp";

const STANDARD_PROCESS_AR = [
  "مكالمة استكشافية لثلاثين دقيقة مع مهندس، لا فريق مبيعات.",
  "ملخص مكتوب من صفحة واحدة: النطاق، المخاطر، نطاق التكلفة، والجدول الزمني.",
  "مرحلة تصميم وهندسة بسعر ثابت.",
  "مراحل بناء كل أسبوعين — برمجيات تعمل في نهاية كل مرحلة.",
  "التسليم، التشغيل، والدعم بموجب اتفاقية خدمة مكتوبة.",
];

function NumberedList({ items }) {
  return (
    <ol className="space-y-5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wafa-cream text-sm font-semibold text-wafa-deep">
            {i + 1}
          </span>
          <div className="pt-1 text-sm leading-relaxed text-wafa-ink/85 lg:text-base">{it}</div>
        </li>
      ))}
    </ol>
  );
}

function Callout({ children }) {
  return (
    <div className="rounded-lg border-s-4 border-wafa-primary bg-wafa-cream/60 px-5 py-4 text-base leading-relaxed text-wafa-deep lg:text-lg">
      {children}
    </div>
  );
}

function StackList({ items }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((t, k) => (
        <span key={k} className="rounded-full border border-wafa-deep/15 bg-white px-3 py-1.5 text-xs font-medium text-wafa-deep">
          {t}
        </span>
      ))}
    </div>
  );
}

function FaqSectionAr({ heading = "أسئلة نتلقاها كثيرًا", items }) {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section data-testid="faq-section-ar" className="bg-white">
      <div className="mx-auto max-w-4xl px-4 py-16 lg:px-6 lg:py-24">
        <h2 className="font-heading text-3xl font-semibold tracking-tight text-wafa-deep sm:text-4xl">
          {heading}
        </h2>
        <div className="mt-8 divide-y divide-wafa-deep/10 border-y border-wafa-deep/10">
          {items.map((it, i) => {
            const open = openIdx === i;
            return (
              <div key={i} data-testid={`faq-item-ar-${i}`}>
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  aria-expanded={open}
                  className="flex w-full items-start justify-between gap-4 py-5 text-start"
                >
                  <span className="font-heading text-base font-semibold text-wafa-deep sm:text-lg">
                    {it.q}
                  </span>
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-wafa-deep/20 text-wafa-deep">
                    {open ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  </span>
                </button>
                {open ? (
                  <div className="pb-6 pe-10 text-sm leading-relaxed text-wafa-ink/80 lg:text-base">
                    {it.a}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RelatedTilesAr({ items }) {
  if (!items?.length) return null;
  return (
    <section className="bg-wafa-cream/40" data-testid="related-tiles-ar">
      <div className="mx-auto max-w-7xl px-4 py-16 lg:px-6 lg:py-24">
        <h2 className="font-heading text-2xl font-semibold tracking-tight text-wafa-deep sm:text-3xl">
          روابط ذات صلة
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {items.map((it, i) => (
            <Link
              key={i}
              to={it.to}
              className="group flex items-start justify-between gap-4 rounded-lg border border-wafa-deep/10 bg-white p-5 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]"
            >
              <div>
                {it.tag ? (
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-wafa-primary">
                    {it.tag}
                  </div>
                ) : null}
                <div className="mt-1 font-heading text-base font-semibold text-wafa-deep group-hover:text-wafa-primary">
                  {it.label}
                </div>
                {it.desc ? <div className="mt-1 text-sm text-wafa-ink/70">{it.desc}</div> : null}
              </div>
              <ArrowLeft className="mt-1 h-4 w-4 text-wafa-deep/60 transition-transform group-hover:-translate-x-1" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ArHero({ eyebrow, h1, intro, breadcrumbs, serviceWhatsApp }) {
  return (
    <section className="bg-wafa-cream/40">
      <div className="mx-auto max-w-5xl px-4 py-16 lg:px-6 lg:py-24">
        {breadcrumbs?.length ? (
          <nav className="mb-6 text-xs text-wafa-ink/60">
            {breadcrumbs.map((b, i) => (
              <span key={i}>
                {i > 0 ? <span className="mx-1">/</span> : null}
                {b.to ? <Link to={b.to} className="hover:text-wafa-deep">{b.label}</Link> : <span className="text-wafa-deep">{b.label}</span>}
              </span>
            ))}
          </nav>
        ) : null}
        {eyebrow ? (
          <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">{eyebrow}</div>
        ) : null}
        <h1 className="mt-3 font-heading text-4xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-5xl lg:text-6xl">
          {h1}
        </h1>
        {intro ? (
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg">{intro}</p>
        ) : null}
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/ar/company/contact"
            className="inline-flex items-center gap-2 rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white hover:bg-wafa-deep"
          >
            احجزوا مكالمة استكشافية مجانية <ArrowLeft className="h-4 w-4" />
          </Link>
          {serviceWhatsApp ? (
            <WhatsAppButton variant="whatsapp" size="lg" label={WA_SERVICE_LABEL.ar} source="service_top" testId="service-whatsapp-top" />
          ) : null}
        </div>
      </div>
    </section>
  );
}

function renderSection(s, i) {
  const tint = i % 2 === 0 ? "white" : "cream";
  switch (s.type) {
    case "block":
      return (
        <ArBlock key={i} tint={s.tint || tint} heading={s.heading}>
          {s.subheading ? (
            <p className="max-w-3xl text-base leading-relaxed text-wafa-ink/80 lg:text-lg">{s.subheading}</p>
          ) : null}
          {s.body ? (
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg">{s.body}</p>
          ) : null}
          {s.paragraphs ? (
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
              {s.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
            </div>
          ) : null}
          {s.bullets ? <div className="mt-6"><ArBullets items={s.bullets} /></div> : null}
          {s.items ? <div className="mt-8"><ArGrid items={s.items} columns={s.columns || 3} /></div> : null}
        </ArBlock>
      );
    case "callout":
      return (
        <ArBlock key={i} tint={s.tint || tint} heading={s.heading}>
          <Callout>{s.text}</Callout>
        </ArBlock>
      );
    case "grid":
      return (
        <ArBlock key={i} tint={s.tint || tint} heading={s.heading}>
          {s.subheading ? (
            <p className={cn("mb-6 max-w-3xl text-base text-wafa-ink/80")}>{s.subheading}</p>
          ) : null}
          <ArGrid items={s.items} columns={s.columns || 3} />
        </ArBlock>
      );
    case "bullets":
      return (
        <ArBlock key={i} tint={s.tint || tint} heading={s.heading}>
          <ArBullets items={s.items} />
        </ArBlock>
      );
    case "numbered":
      return (
        <ArBlock key={i} tint={s.tint || tint} heading={s.heading}>
          <NumberedList items={s.items} />
        </ArBlock>
      );
    case "stack":
      return (
        <ArBlock key={i} tint={s.tint || tint} heading={s.heading}>
          <StackList items={s.items} />
        </ArBlock>
      );
    case "process":
      return (
        <ArBlock key={i} tint={s.tint || tint} heading={s.heading || "كيف نعمل"}>
          <NumberedList items={s.items || STANDARD_PROCESS_AR} />
        </ArBlock>
      );
    case "faq":
      return <FaqSectionAr key={i} heading={s.heading} items={s.items} />;
    default:
      return null;
  }
}

/**
 * Arabic inner page — mirrors <InnerPage /> for /ar/* service and industry
 * pages. `hreflangEn` accepts an explicit English path when the AR slug
 * differs from the English slug (used by AR industry pages).
 */
export function ArInnerPage({ eyebrow, h1, intro, breadcrumbs, meta, sections, related, hreflangEn, cta, serviceWhatsApp = false }) {
  return (
    <div data-testid="ar-inner-page">
      <Seo title={meta?.title} description={meta?.description} hreflangEn={hreflangEn} />
      <ArHero eyebrow={eyebrow} h1={h1} intro={intro} breadcrumbs={breadcrumbs} serviceWhatsApp={serviceWhatsApp} />
      {sections?.map((s, i) => renderSection(s, i))}
      <RelatedTilesAr items={related} />
      <ArContactCTA heading={cta?.heading || "جاهزون للحديث؟"} serviceWhatsApp={serviceWhatsApp} />
    </div>
  );
}
